import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import api, { BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import SharedNavigation from "@/components/SharedNavigation";
import FeaturedBusinessesCarousel from "@/components/FeaturedBusinessesCarousel";
import LivePromoCard from "@/components/LivePromoCard";
import { getCoverUrl, getProfilePhotoUrl } from "@/lib/mediaUrls";
import { getDisplayPlays, formatDisplayPlays } from "@/lib/promoPlays";

// Microphone Icon Component
const MicIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/>
    <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
  </svg>
);

// Play Button Component
const PlayButton = ({ onClick, size = "md" }) => {
  const sizes = {
    sm: "w-8 h-8",
    md: "w-12 h-12",
    lg: "w-16 h-16"
  };
  return (
    <button
      onClick={onClick}
      className={`${sizes[size]} rounded-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 flex items-center justify-center shadow-lg hover:shadow-purple-500/30 transition-all transform hover:scale-105`}
    >
      <svg className="w-1/2 h-1/2 text-white ml-0.5" fill="currentColor" viewBox="0 0 20 20">
        <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
      </svg>
    </button>
  );
};

// Badge Component
const Badge = ({ type }) => {
  const badges = {
    founder: { label: "Founder", className: "bg-gradient-to-r from-yellow-500 to-amber-600 text-black" },
    admin: { label: "Admin", className: "bg-purple-600 text-white" },
    spotlight_winner: { label: "Spotlight", className: "bg-gradient-to-r from-pink-500 to-purple-600 text-white" }
  };
  const badge = badges[type];
  if (!badge) return null;
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${badge.className}`}>
      {badge.label}
    </span>
  );
};

const LandingPage = () => {
  const navigate = useNavigate();
  // Initialize from localStorage to prevent flash of unauthenticated state
  const [currentUser, setCurrentUser] = useState(() => {
    const token = localStorage.getItem('session_token');
    return token ? { _loading: true } : null;
  });
  const [recentSubmissions, setRecentSubmissions] = useState([]);
  const [weeklyTop3, setWeeklyTop3] = useState([]);
  const [featuredArtists, setFeaturedArtists] = useState([]);
  const [founderShowcase, setFounderShowcase] = useState([]);
  const [trendingTracks, setTrendingTracks] = useState([]);
  const [liveSettings, setLiveSettings] = useState(null);
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  // Phase 21.3a5 Batch 2 — admin-configurable weekly timer + live promo card.
  const [homepageCfg, setHomepageCfg] = useState(null);
  const [waitlistForm, setWaitlistForm] = useState({ name: "", artist_name: "", email: "", improvement_area: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchHomepageConfig = useCallback(async () => {
    try {
      const r = await api.get('/homepage/config');
      setHomepageCfg(r.data || null);
    } catch (_e) { /* non-critical */ }
  }, []);

  const fetchCurrentUser = useCallback(async () => {
    try {
      const response = await api.get('/auth/me');
      setCurrentUser(response.data);
    } catch (error) {
      // User not logged in or session expired
      setCurrentUser(null);
    }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const [recentRes, top3Res, featuredRes, founderRes, liveRes, trendingRes] = await Promise.all([
        api.get('/submissions/recent').catch(() => ({ data: [] })),
        api.get('/submissions/top3').catch(() => ({ data: [] })),
        api.get('/submissions/featured').catch(() => ({ data: [] })),
        api.get('/submissions/founder-showcase').catch(() => ({ data: [] })),
        api.get('/live/settings').catch(() => ({ data: null })),
        api.get('/charts/trending?limit=6').catch(() => ({ data: { tracks: [] } }))
      ]);

      setRecentSubmissions(recentRes.data?.slice(0, 8) || []);
      setWeeklyTop3(top3Res.data || []);
      setFeaturedArtists(featuredRes.data?.slice(0, 6) || []);
      setFounderShowcase(founderRes.data || []);
      setLiveSettings(liveRes.data);
      setTrendingTracks(trendingRes.data?.tracks || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  }, []);

  const updateCountdown = useCallback(() => {
    const now = new Date();
    // Phase 21.3a5 Batch 2 — honor admin-configured deadline (UTC day/hour/min).
    const wt = homepageCfg?.weekly_timer;
    const day = Number.isFinite(wt?.deadline_day_of_week) ? wt.deadline_day_of_week : 4; // Fri default
    const hour = Number.isFinite(wt?.deadline_hour_utc) ? wt.deadline_hour_utc : 23;
    const minute = Number.isFinite(wt?.deadline_minute_utc) ? wt.deadline_minute_utc : 59;
    // Map Monday-based (0..6) → JS getDay Sunday-based (0..6).
    const targetJsDay = (day + 1) % 7;
    const target = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), hour, minute, 0, 0));
    const daysUntil = (targetJsDay - target.getUTCDay() + 7) % 7;
    target.setUTCDate(target.getUTCDate() + daysUntil);
    if (target - now <= 0) target.setUTCDate(target.getUTCDate() + 7);

    const diff = target - now;
    if (diff > 0) {
      setCountdown({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((diff % (1000 * 60)) / 1000)
      });
    }
  }, [homepageCfg]);

  useEffect(() => {
    fetchData();
    fetchCurrentUser();
    fetchHomepageConfig();
  }, [fetchData, fetchCurrentUser, fetchHomepageConfig]);

  useEffect(() => {
    const timer = setInterval(updateCountdown, 1000);
    return () => clearInterval(timer);
  }, [updateCountdown]);

  const handleWaitlistSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await api.post('/waitlist', waitlistForm);
      toast.success("Welcome to the Studio!", { description: "You're on the waitlist." });
      setWaitlistForm({ name: "", artist_name: "", email: "", improvement_area: "" });
    } catch (error) {
      toast.error("Something went wrong", { description: "Please try again." });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePlayTrack = (submission) => {
    // Navigate to track page instead of opening raw file
    navigate(`/track/${submission.submission_id}`);
  };

  const getTrackTitle = (sub) => sub.track_title || sub.artist_name || 'New Track';

  // Combine founder showcase with recent for display, avoiding duplicates
  const founderIds = new Set(founderShowcase.map(s => s.submission_id));
  const filteredRecent = recentSubmissions.filter(s => !founderIds.has(s.submission_id));
  const displaySubmissions = [...founderShowcase, ...filteredRecent].slice(0, 8);

  return (
    <>
      <Helmet>
        <title>RGM Studio – Global Artist Platform</title>
        <meta name="description" content="Join RGM Studio to upload music, get featured, and grow your fanbase worldwide." />
        <meta property="og:title" content="RGM Studio – Global Artist Platform" />
        <meta property="og:description" content="Join RGM Studio to upload music, get featured, and grow your fanbase worldwide." />
        <meta property="og:image" content="https://www.rgmstudio.org/rgm-og-image.png" />
        <meta property="og:url" content="https://www.rgmstudio.org/" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="RGM Studio – Global Artist Platform" />
        <meta name="twitter:description" content="Join RGM Studio to upload music, get featured, and grow your fanbase worldwide." />
        <meta name="twitter:image" content="https://www.rgmstudio.org/rgm-og-image.png" />
      </Helmet>
      <div className="min-h-screen bg-[#08080c]">
        {/* Navigation */}
        <SharedNavigation currentUser={currentUser} activePage="home" />

      {/* Hero Section */}
      <section className="pt-28 pb-24 px-4 relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <div className="absolute top-20 left-1/4 w-[500px] h-[500px] bg-purple-600/10 rounded-full blur-[100px]"></div>
          <div className="absolute bottom-20 right-1/4 w-[400px] h-[400px] bg-pink-600/10 rounded-full blur-[100px]"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-purple-900/5 rounded-full blur-[120px]"></div>
        </div>
        
        <div className="max-w-7xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 mb-8 px-4 py-2 bg-purple-900/30 border border-purple-500/30 rounded-full">
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
            <span className="text-purple-300 text-sm font-medium">Now Live • Submit Your Music Free</span>
          </div>
          
          <h1 className="text-5xl sm:text-7xl lg:text-8xl font-black text-white mb-8 tracking-tight leading-tight" data-testid="hero-title">
            Where Independent<br/>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-500 to-purple-400">Artists Rise</span>
          </h1>
          
          <p className="text-xl sm:text-2xl text-gray-400 mb-12 max-w-3xl mx-auto leading-relaxed">
            Submit your music. Get scored by industry professionals.<br className="hidden sm:block"/>
            Top 3 artists featured weekly across our global community.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/discover">
              <Button size="lg" className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-bold px-10 py-7 text-lg shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 transition-all" data-testid="hero-discover-btn">
                <span className="mr-2">🎧</span>
                Start Discovering
              </Button>
            </Link>
            <Link to="/login">
              <Button size="lg" variant="outline" className="border-2 border-purple-500/50 text-purple-300 hover:bg-purple-500/10 hover:border-purple-400 px-10 py-7 text-lg font-semibold transition-all" data-testid="hero-submit-btn">
                <MicIcon className="w-5 h-5 mr-2" />
                Submit Your Music
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Phase 21.3a5 Batch 2 — 📡 RGM Studio Live Promo Card (admin-controlled) */}
      <div className="max-w-6xl mx-auto px-4 mt-4">
        <LivePromoCard />
      </div>

      {/* Countdown Section — honors admin toggle */}
      {homepageCfg?.weekly_timer?.enabled !== false && (
      <section className="py-16 px-4 bg-gradient-to-r from-purple-900/20 via-black to-pink-900/20 border-y border-purple-500/20" data-testid="countdown-section">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-white mb-3">Weekly Submission Window</h2>
          <p className="text-gray-400 mb-8">Submissions close every Friday at midnight EST. Top 3 selected weekly.</p>
          
          <div className="flex justify-center gap-4 sm:gap-8 mb-8">
            {[
              { label: 'Days', value: countdown.days },
              { label: 'Hours', value: countdown.hours },
              { label: 'Minutes', value: countdown.minutes },
              { label: 'Seconds', value: countdown.seconds }
            ].map((item) => (
              <div key={item.label} className="text-center">
                <div className="w-16 sm:w-24 h-16 sm:h-24 bg-black/60 border border-purple-500/30 rounded-2xl flex items-center justify-center mb-2 shadow-lg shadow-purple-500/10">
                  <span className="text-3xl sm:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-b from-white to-purple-300" data-testid={`countdown-${item.label.toLowerCase()}`}>
                    {String(item.value).padStart(2, '0')}
                  </span>
                </div>
                <span className="text-xs sm:text-sm text-gray-500 uppercase tracking-wider font-medium">{item.label}</span>
              </div>
            ))}
          </div>
          
          <Link to="/login">
            <Button className="bg-white text-black hover:bg-gray-100 font-bold px-8 py-3 shadow-lg">
              Submit Before Deadline
            </Button>
          </Link>
        </div>
      </section>
      )}

      {/* RGM Studio Live Section */}
      <section className="py-16 px-4 bg-gradient-to-b from-red-900/20 via-black to-black" data-testid="live-section">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8 p-8 bg-gradient-to-r from-red-900/30 to-purple-900/30 border border-red-500/30 rounded-3xl">
            <div className="flex items-center gap-6">
              <div className="relative">
                <div className="w-16 h-16 bg-red-600 rounded-2xl flex items-center justify-center">
                  <div className="w-4 h-4 bg-white rounded-full"></div>
                </div>
                <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full animate-ping"></div>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white mb-1">RGM Studio Live</h3>
                <p className="text-gray-300">{liveSettings?.is_live ? "We're live right now!" : "Live listening sessions & artist showcases"}</p>
                <p className="text-gray-500 text-sm mt-1">Community voting • Artist reviews • Exclusive premieres</p>
              </div>
            </div>
            <Link to="/live">
              <Button className={`px-8 py-3 font-bold ${liveSettings?.is_live ? 'bg-red-600 hover:bg-red-500 animate-pulse' : 'bg-gradient-to-r from-red-600 to-purple-600 hover:from-red-500 hover:to-purple-500'}`} data-testid="live-watch-btn">
                {liveSettings?.is_live ? '🔴 Join Live Now' : 'View Schedule'}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Weekly Top 3 Section */}
      <section className="py-20 px-4 bg-gradient-to-b from-black via-purple-900/10 to-black" data-testid="weekly-top3">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-500/20 border border-yellow-500/30 rounded-full text-yellow-300 text-sm font-bold mb-4">
              <span className="text-lg">👑</span> Weekly Top 3
            </span>
            <h2 className="text-3xl sm:text-4xl font-black text-white mb-4">This Week's Champions</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">Highest scored artists selected by our A&R team</p>
          </div>
          
          {weeklyTop3.length > 0 || founderShowcase.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {(weeklyTop3.length > 0 ? weeklyTop3 : founderShowcase.slice(0, 3)).map((artist, index) => (
                <Card 
                  key={artist.submission_id || index}
                  className={`group relative overflow-hidden bg-gradient-to-b from-gray-900/80 to-black/80 border-2 ${
                    index === 0 ? 'border-yellow-500/50 shadow-lg shadow-yellow-500/20' : 
                    index === 1 ? 'border-gray-400/50 shadow-lg shadow-gray-400/20' : 
                    'border-amber-700/50 shadow-lg shadow-amber-700/20'
                  } hover:border-purple-500/50 transition-all duration-500 rounded-2xl`}
                  data-testid={`top3-card-${index}`}
                >
                  {/* Rank Badge */}
                  <div className="absolute top-4 left-4 z-20">
                    <span className={`inline-flex items-center justify-center w-12 h-12 rounded-xl font-black text-xl ${
                      index === 0 ? 'bg-gradient-to-br from-yellow-400 to-yellow-600 text-black shadow-lg shadow-yellow-500/50' : 
                      index === 1 ? 'bg-gradient-to-br from-gray-300 to-gray-500 text-black shadow-lg' : 
                      'bg-gradient-to-br from-amber-600 to-amber-800 text-white shadow-lg'
                    }`}>
                      #{index + 1}
                    </span>
                  </div>
                  
                  {/* Founder/Showcase Badge */}
                  {(artist.is_founder_showcase || artist.user_badges?.includes('founder')) && (
                    <div className="absolute top-4 right-4 z-20">
                      <span className="px-3 py-1 bg-gradient-to-r from-yellow-500 to-amber-600 text-black text-xs font-bold rounded-full shadow-lg">
                        Founder Showcase
                      </span>
                    </div>
                  )}
                  
                  {/* Cover Art */}
                  <div className="aspect-square relative overflow-hidden">
                    <img 
                      src={artist.cover_art || artist.cover_url
                        ? getCoverUrl(artist)
                        : 'https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?w=400&h=400&fit=crop'}
                      alt={artist.artist_name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?w=400&h=400&fit=crop'; }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent"></div>
                    
                    {/* Play Button Overlay */}
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <PlayButton onClick={() => handlePlayTrack(artist)} size="lg" />
                    </div>
                  </div>
                  
                  {/* Info */}
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-white mb-1 truncate">{getTrackTitle(artist)}</h3>
                    <p className="text-purple-400 font-semibold mb-3">{artist.artist_name}</p>
                    
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm text-gray-400 bg-gray-800/50 px-3 py-1 rounded-full">{artist.genre}</span>
                      <span className={`text-lg font-bold ${
                        (artist.score || 0) >= 45 ? 'text-green-400' : 
                        (artist.score || 0) >= 30 ? 'text-yellow-400' : 'text-gray-400'
                      }`}>
                        {artist.score || 0}/60
                      </span>
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex gap-2">
                      <Link to={`/artist/${artist.user_id}`} className="flex-1">
                        <Button variant="outline" size="sm" className="w-full border-purple-500/50 text-purple-300 hover:bg-purple-500/20">
                          View Profile
                        </Button>
                      </Link>
                      <Button 
                        size="sm" 
                        onClick={() => handlePlayTrack(artist)}
                        className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500"
                      >
                        Play
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-gradient-to-b from-gray-900/30 to-black/30 rounded-3xl border border-gray-800">
              <div className="text-6xl mb-4">🎤</div>
              <p className="text-xl text-gray-400 mb-2">First week of competition!</p>
              <p className="text-gray-500 mb-6">Submit your track to be in our first Top 3.</p>
              <Link to="/login">
                <Button className="bg-gradient-to-r from-purple-600 to-pink-600">Be the First Champion</Button>
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Recent Submissions Section */}
      <section className="py-20 px-4 bg-black" data-testid="recent-submissions">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-12">
            <div>
              <h2 className="text-3xl sm:text-4xl font-black text-white mb-2">Fresh Drops</h2>
              <p className="text-gray-400">Latest submissions from independent artists worldwide</p>
            </div>
            <Link to="/featured">
              <Button variant="outline" className="border-purple-500/50 text-purple-300 hover:bg-purple-500/20 font-semibold">
                View All
              </Button>
            </Link>
          </div>
          
          {displaySubmissions.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {displaySubmissions.map((submission, index) => (
                <Link 
                  key={submission.submission_id || index} 
                  to={`/track/${submission.submission_id}`}
                  className="group"
                  data-testid={`recent-track-${index}`}
                >
                  <div className="aspect-square rounded-2xl overflow-hidden mb-4 relative bg-gray-900 shadow-lg">
                    <img 
                      src={submission.cover_art || submission.cover_url
                        ? getCoverUrl(submission)
                        : 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=200&h=200&fit=crop'}
                      alt={submission.artist_name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=200&h=200&fit=crop'; }}
                    />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <PlayButton onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate(`/track/${submission.submission_id}`); }} size="md" />
                    </div>
                    
                    {/* Badges */}
                    {submission.is_founder_showcase && (
                      <div className="absolute top-2 left-2">
                        <span className="px-2 py-1 bg-gradient-to-r from-yellow-500 to-amber-600 text-black text-xs font-bold rounded-full">
                          Founder
                        </span>
                      </div>
                    )}
                  </div>
                  <h4 className="text-white font-semibold truncate group-hover:text-purple-400 transition-colors">
                    {getTrackTitle(submission)}
                  </h4>
                  <p className="text-gray-500 text-sm truncate">{submission.artist_name}</p>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-gray-900/30 rounded-3xl border border-gray-800">
              <div className="text-5xl mb-4">🎵</div>
              <p className="text-gray-400 text-lg mb-2">No submissions yet</p>
              <p className="text-gray-500 mb-6">Be the first to drop your track!</p>
              <Link to="/login">
                <Button className="bg-gradient-to-r from-purple-600 to-pink-600">Submit Now</Button>
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Trending Now Section */}
      {trendingTracks.length > 0 && (
        <section className="py-20 px-4 bg-gradient-to-b from-purple-900/10 to-black" data-testid="trending-section">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-3xl sm:text-4xl font-black text-white flex items-center gap-3">
                  <span className="text-3xl">🔥</span>
                  Trending Now
                </h2>
                <p className="text-gray-400 mt-2">Hot tracks making waves on RGM Studio</p>
              </div>
              <Link to="/charts">
                <Button variant="outline" className="border-purple-500/50 text-purple-300 hover:bg-purple-500/20">
                  View All Charts
                </Button>
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {trendingTracks.slice(0, 6).map((track, idx) => (
                <Link key={track.submission_id} to={`/track/${track.submission_id}`}>
                  <Card 
                    className="flex items-center gap-4 p-4 bg-gray-900/50 border-purple-900/30 hover:border-purple-500/50 hover:bg-gray-900/70 transition-all group"
                    data-testid={`trending-track-${idx}`}
                  >
                    {/* Rank */}
                    <div className={`w-8 h-8 flex items-center justify-center font-bold text-lg flex-shrink-0 ${
                      idx < 3 ? 'text-yellow-400' : 'text-gray-500'
                    }`}>
                      {idx < 3 ? ['🥇', '🥈', '🥉'][idx] : idx + 1}
                    </div>

                    {/* Cover */}
                    <div className="w-14 h-14 rounded-lg overflow-hidden flex-shrink-0">
                      <img
                        src={getCoverUrl(track)}
                        alt={track.track_title}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=100&h=100&fit=crop'; }}
                      />
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <h4 className="text-white font-semibold truncate group-hover:text-purple-300 transition-colors">
                        {track.track_title || 'Untitled'}
                      </h4>
                      <p className="text-gray-400 text-sm truncate">{track.artist_display_name}</p>
                    </div>

                    {/* Stats */}
                    <div className="flex items-center gap-3 text-sm text-gray-500 flex-shrink-0">
                      <span className="flex items-center gap-1" title="Real listener plays">
                        <span>▶️</span>
                        {formatDisplayPlays(getDisplayPlays(track))}
                      </span>
                      <span className="flex items-center gap-1" title="Likes">
                        <span>❤️</span>
                        {(track.likes_count || 0)}
                      </span>
                    </div>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Featured Businesses (Phase 6) */}
      <FeaturedBusinessesCarousel variant="landing" />

      {/* Featured Artists Section */}
      <section className="py-20 px-4 bg-gradient-to-b from-black to-purple-900/10" data-testid="featured-artists">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-black text-white mb-4">Featured Artists</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">Artists recognized by RGM Studio for exceptional talent and potential</p>
          </div>
          
          {(featuredArtists.length > 0 || founderShowcase.length > 0) ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {(featuredArtists.length > 0 ? featuredArtists : founderShowcase).slice(0, 6).map((artist, index) => (
                <Card 
                  key={artist.submission_id || index}
                  className="group bg-gradient-to-br from-purple-900/30 via-gray-900/50 to-pink-900/30 border border-purple-500/20 hover:border-purple-500/50 transition-all duration-300 rounded-2xl overflow-hidden"
                  data-testid={`featured-artist-${index}`}
                >
                  <div className="p-6">
                    <div className="flex items-start gap-4 mb-4">
                      <div className="w-20 h-20 rounded-xl overflow-hidden border-2 border-purple-500/50 flex-shrink-0">
                        <img 
                          src={getCoverUrl(artist)}
                          alt={artist.artist_name}
                          className="w-full h-full object-cover"
                          onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=100&h=100&fit=crop'; }}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-lg font-bold text-white truncate">{artist.artist_name}</h3>
                          {artist.user_badges?.includes('founder') && <Badge type="founder" />}
                        </div>
                        <p className="text-purple-400 text-sm font-medium mb-1">{artist.genre}</p>
                        <p className="text-gray-500 text-sm truncate">{getTrackTitle(artist)}</p>
                      </div>
                    </div>
                    
                    {artist.description && (
                      <p className="text-gray-400 text-sm line-clamp-2 mb-4">{artist.description}</p>
                    )}
                    
                    {/* Action Buttons */}
                    <div className="flex gap-2">
                      <Link to={`/artist/${artist.user_id}`} className="flex-1">
                        <Button variant="outline" size="sm" className="w-full border-purple-500/50 text-purple-300 hover:bg-purple-500/20">
                          View Profile
                        </Button>
                      </Link>
                      <Button 
                        size="sm" 
                        onClick={() => handlePlayTrack(artist)}
                        className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 px-4"
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                        </svg>
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-gray-900/30 rounded-3xl border border-gray-800">
              <p className="text-gray-400 text-lg">Featured artists coming soon!</p>
            </div>
          )}
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-4 bg-black" data-testid="how-it-works">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-black text-white text-center mb-16">How RGM Studio Works</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {[
              { step: '01', title: 'Submit Your Track', desc: 'Upload your music, cover art, and tell us about yourself. Submissions are FREE during beta.', icon: '🎤' },
              { step: '02', title: 'Get Scored', desc: 'Our A&R team scores your track on lyrics, flow, originality, mix quality, branding, and growth potential.', icon: '📊' },
              { step: '03', title: 'Get Featured', desc: 'Top 3 artists each week get featured across RGM platforms, community exposure, and exclusive opportunities.', icon: '🌟' }
            ].map((item, index) => (
              <div key={index} className="text-center group" data-testid={`how-step-${index}`}>
                <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center text-4xl shadow-lg shadow-purple-500/30 group-hover:shadow-purple-500/50 transition-all group-hover:scale-110">
                  {item.icon}
                </div>
                <div className="text-purple-500 font-mono text-sm mb-2">{item.step}</div>
                <h3 className="text-xl font-bold text-white mb-3">{item.title}</h3>
                <p className="text-gray-400 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Waitlist CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-b from-black to-purple-900/20" data-testid="waitlist-section">
        <div className="max-w-2xl mx-auto">
          <Card className="bg-gradient-to-br from-purple-900/50 via-gray-900/80 to-pink-900/50 border border-purple-500/30 p-8 rounded-3xl shadow-2xl">
            <h2 className="text-3xl font-black text-white text-center mb-3">Join the Movement</h2>
            <p className="text-gray-400 text-center mb-8">Get early access to new features, exclusive drops, and community events.</p>
            
            <form onSubmit={handleWaitlistSubmit} className="space-y-4" data-testid="waitlist-form">
              <div className="grid grid-cols-2 gap-4">
                <Input
                  placeholder="Your Name"
                  value={waitlistForm.name}
                  onChange={(e) => setWaitlistForm({ ...waitlistForm, name: e.target.value })}
                  required
                  className="bg-black/50 border-purple-500/30 text-white placeholder:text-gray-500 rounded-xl"
                  data-testid="waitlist-name"
                />
                <Input
                  placeholder="Artist Name"
                  value={waitlistForm.artist_name}
                  onChange={(e) => setWaitlistForm({ ...waitlistForm, artist_name: e.target.value })}
                  required
                  className="bg-black/50 border-purple-500/30 text-white placeholder:text-gray-500 rounded-xl"
                  data-testid="waitlist-artist"
                />
              </div>
              <Input
                type="email"
                placeholder="Email Address"
                value={waitlistForm.email}
                onChange={(e) => setWaitlistForm({ ...waitlistForm, email: e.target.value })}
                required
                className="bg-black/50 border-purple-500/30 text-white placeholder:text-gray-500 rounded-xl"
                data-testid="waitlist-email"
              />
              <Textarea
                placeholder="What do you want to improve as an artist?"
                value={waitlistForm.improvement_area}
                onChange={(e) => setWaitlistForm({ ...waitlistForm, improvement_area: e.target.value })}
                required
                rows={3}
                className="bg-black/50 border-purple-500/30 text-white placeholder:text-gray-500 rounded-xl"
                data-testid="waitlist-improvement"
              />
              <Button 
                type="submit" 
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-bold py-4 rounded-xl shadow-lg"
                data-testid="waitlist-submit"
              >
                {isSubmitting ? 'Joining...' : 'Join Waitlist'}
              </Button>
            </form>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 border-t border-gray-800 bg-black" data-testid="footer">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <Link to="/" className="flex items-center gap-2">
              <img src="/rgm-logo.png" alt="RGM Studio" className="h-8 w-auto object-contain" />
            </Link>
            <div className="flex items-center gap-8 text-gray-400 text-sm">
              <Link to="/featured" className="hover:text-white transition-colors">Featured</Link>
              <Link to="/magazine" className="hover:text-white transition-colors">Magazine</Link>
              <Link to="/live" className="hover:text-white transition-colors">Live</Link>
              <Link to="/guidelines" className="hover:text-white transition-colors">Guidelines</Link>
            </div>
            <p className="text-gray-600 text-sm">© 2026 RGM Studio. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
    </>
  );
};

export default LandingPage;
